import { RegistroDto, UpdateRegistroDto } from "@/api";
import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import { isNil, keyBy } from "lodash";
import type { TFunction } from "i18next";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

// Machine type options
const TIPOLOGIA_MACCHINA_OPTIONS = [
  {
    id: 1,
    getLabel: (t: TFunction) =>
      t("praevidereVibratio35.tipologia.motoreRigido"),
  },
  {
    id: 2,
    getLabel: (t: TFunction) =>
      t("praevidereVibratio35.tipologia.motoreElastico"),
  },
  {
    id: 3,
    getLabel: (t: TFunction) => t("praevidereVibratio35.tipologia.pompa"),
  },
  {
    id: 4,
    getLabel: (t: TFunction) => t("praevidereVibratio35.tipologia.riduttore"),
  },
  {
    id: 5,
    getLabel: (t: TFunction) => t("praevidereVibratio35.tipologia.mandrino"),
  },
  {
    id: 6,
    getLabel: (t: TFunction) =>
      t("praevidereVibratio35.tipologia.viteMacchina"),
  },
  {
    id: 7,
    getLabel: (t: TFunction) => t("praevidereVibratio35.tipologia.telaio"),
  },
  {
    id: 8,
    getLabel: (t: TFunction) =>
      t("praevidereVibratio35.tipologia.motoriIdraulici"),
  },
];

export type Registri_PRAEVIDERE_VIBRATIO_35 = {
  tipologia_macchina?: number;
  velocita_rotazione?: number;

  // Allarmi RMS
  rms_i_1?: number; // verde
  rms_i_2?: number; // giallo
  rms_i_3?: number; // arancio
  rms_i_4?: number; // rosso

  rms_ii_1?: number; // verde
  rms_ii_2?: number; // giallo
  rms_ii_3?: number; // arancio
  rms_ii_4?: number; // rosso

  rms_iii_1?: number; // verde
  rms_iii_2?: number; // giallo
  rms_iii_3?: number; // arancio
  rms_iii_4?: number; // rosso

  rms_iv_1?: number; // verde
  rms_iv_2?: number; // giallo
  rms_iv_3?: number; // arancio
  rms_iv_4?: number; // rosso

  // Allarmi FFT
  fft_f1_armonica?: number;
  fft_f1_soglia_1?: number;
  fft_f1_soglia_2?: number;
  fft_f1_guasto?: string;

  fft_f2_armonica?: number;
  fft_f2_soglia_1?: number;
  fft_f2_soglia_2?: number;
  fft_f2_guasto?: string;

  fft_f3_armonica?: number;
  fft_f3_soglia_1?: number;
  fft_f3_soglia_2?: number;
  fft_f3_guasto?: string;

  fft_f4_armonica?: number;
  fft_f4_soglia_1?: number;
  fft_f4_soglia_2?: number;
  fft_f4_guasto?: string;

  fft_f5_armonica?: number;
  fft_f5_soglia_1?: number;
  fft_f5_soglia_2?: number;
  fft_f5_guasto?: string;
};

export function registriToForm_PRAEVIDERE_VIBRATIO_35(
  _registri: RegistroDto[],
): {
  [r in keyof Registri_PRAEVIDERE_VIBRATIO_35]: any;
} {
  const registri = keyBy(_registri, "registro");

  return {
    // Machine Type settings
    tipologia_macchina: isNil(registri["tipologia_macchina"])
      ? undefined
      : parseInt(registri["tipologia_macchina"].value),
    velocita_rotazione: isNil(registri["velocita_rotazione"])
      ? undefined
      : parseFloat(registri["velocita_rotazione"].value),

    // Class I RMS values
    rms_i_1: isNil(registri["rms_i_1"])
      ? undefined
      : parseFloat(registri["rms_i_1"].value),
    rms_i_2: isNil(registri["rms_i_2"])
      ? undefined
      : parseFloat(registri["rms_i_2"].value),
    rms_i_3: isNil(registri["rms_i_3"])
      ? undefined
      : parseFloat(registri["rms_i_3"].value),
    rms_i_4: isNil(registri["rms_i_4"])
      ? undefined
      : parseFloat(registri["rms_i_4"].value),

    // Class II RMS values
    rms_ii_1: isNil(registri["rms_ii_1"])
      ? undefined
      : parseFloat(registri["rms_ii_1"].value),
    rms_ii_2: isNil(registri["rms_ii_2"])
      ? undefined
      : parseFloat(registri["rms_ii_2"].value),
    rms_ii_3: isNil(registri["rms_ii_3"])
      ? undefined
      : parseFloat(registri["rms_ii_3"].value),
    rms_ii_4: isNil(registri["rms_ii_4"])
      ? undefined
      : parseFloat(registri["rms_ii_4"].value),

    // Class III RMS values
    rms_iii_1: isNil(registri["rms_iii_1"])
      ? undefined
      : parseFloat(registri["rms_iii_1"].value),
    rms_iii_2: isNil(registri["rms_iii_2"])
      ? undefined
      : parseFloat(registri["rms_iii_2"].value),
    rms_iii_3: isNil(registri["rms_iii_3"])
      ? undefined
      : parseFloat(registri["rms_iii_3"].value),
    rms_iii_4: isNil(registri["rms_iii_4"])
      ? undefined
      : parseFloat(registri["rms_iii_4"].value),

    // Class IV RMS values
    rms_iv_1: isNil(registri["rms_iv_1"])
      ? undefined
      : parseFloat(registri["rms_iv_1"].value),
    rms_iv_2: isNil(registri["rms_iv_2"])
      ? undefined
      : parseFloat(registri["rms_iv_2"].value),
    rms_iv_3: isNil(registri["rms_iv_3"])
      ? undefined
      : parseFloat(registri["rms_iv_3"].value),
    rms_iv_4: isNil(registri["rms_iv_4"])
      ? undefined
      : parseFloat(registri["rms_iv_4"].value),

    // FFT Alarm values
    fft_f1_armonica: isNil(registri["fft_f1_armonica"])
      ? undefined
      : parseFloat(registri["fft_f1_armonica"].value),
    fft_f1_soglia_1: isNil(registri["fft_f1_soglia_1"])
      ? undefined
      : parseFloat(registri["fft_f1_soglia_1"].value),
    fft_f1_soglia_2: isNil(registri["fft_f1_soglia_2"])
      ? undefined
      : parseFloat(registri["fft_f1_soglia_2"].value),
    fft_f1_guasto: isNil(registri["fft_f1_guasto"])
      ? undefined
      : registri["fft_f1_guasto"].value,

    fft_f2_armonica: isNil(registri["fft_f2_armonica"])
      ? undefined
      : parseFloat(registri["fft_f2_armonica"].value),
    fft_f2_soglia_1: isNil(registri["fft_f2_soglia_1"])
      ? undefined
      : parseFloat(registri["fft_f2_soglia_1"].value),
    fft_f2_soglia_2: isNil(registri["fft_f2_soglia_2"])
      ? undefined
      : parseFloat(registri["fft_f2_soglia_2"].value),
    fft_f2_guasto: isNil(registri["fft_f2_guasto"])
      ? undefined
      : registri["fft_f2_guasto"].value,

    fft_f3_armonica: isNil(registri["fft_f3_armonica"])
      ? undefined
      : parseFloat(registri["fft_f3_armonica"].value),
    fft_f3_soglia_1: isNil(registri["fft_f3_soglia_1"])
      ? undefined
      : parseFloat(registri["fft_f3_soglia_1"].value),
    fft_f3_soglia_2: isNil(registri["fft_f3_soglia_2"])
      ? undefined
      : parseFloat(registri["fft_f3_soglia_2"].value),
    fft_f3_guasto: isNil(registri["fft_f3_guasto"])
      ? undefined
      : registri["fft_f3_guasto"].value,

    fft_f4_armonica: isNil(registri["fft_f4_armonica"])
      ? undefined
      : parseFloat(registri["fft_f4_armonica"].value),
    fft_f4_soglia_1: isNil(registri["fft_f4_soglia_1"])
      ? undefined
      : parseFloat(registri["fft_f4_soglia_1"].value),
    fft_f4_soglia_2: isNil(registri["fft_f4_soglia_2"])
      ? undefined
      : parseFloat(registri["fft_f4_soglia_2"].value),
    fft_f4_guasto: isNil(registri["fft_f4_guasto"])
      ? undefined
      : registri["fft_f4_guasto"].value,

    fft_f5_armonica: isNil(registri["fft_f5_armonica"])
      ? undefined
      : parseFloat(registri["fft_f5_armonica"].value),
    fft_f5_soglia_1: isNil(registri["fft_f5_soglia_1"])
      ? undefined
      : parseFloat(registri["fft_f5_soglia_1"].value),
    fft_f5_soglia_2: isNil(registri["fft_f5_soglia_2"])
      ? undefined
      : parseFloat(registri["fft_f5_soglia_2"].value),
    fft_f5_guasto: isNil(registri["fft_f5_guasto"])
      ? undefined
      : registri["fft_f5_guasto"].value,
  };
}

export function formToRegistri_PRAEVIDERE_VIBRATIO_35(
  form: Registri_PRAEVIDERE_VIBRATIO_35,
): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_PRAEVIDERE_VIBRATIO_35]?: UpdateRegistroDto;
  } = {
    // Machine Type settings
    tipologia_macchina: isNil(form.tipologia_macchina)
      ? undefined
      : {
          registro: "tipologia_macchina",
          topicPrefix,
          value: form.tipologia_macchina,
        },
    velocita_rotazione: isNil(form.velocita_rotazione)
      ? undefined
      : {
          registro: "velocita_rotazione",
          topicPrefix,
          value: form.velocita_rotazione,
        },

    // Class I RMS values
    rms_i_1: isNil(form.rms_i_1)
      ? undefined
      : {
          registro: "rms_i_1",
          topicPrefix,
          value: form.rms_i_1,
        },
    rms_i_2: isNil(form.rms_i_2)
      ? undefined
      : {
          registro: "rms_i_2",
          topicPrefix,
          value: form.rms_i_2,
        },
    rms_i_3: isNil(form.rms_i_3)
      ? undefined
      : {
          registro: "rms_i_3",
          topicPrefix,
          value: form.rms_i_3,
        },
    rms_i_4: isNil(form.rms_i_4)
      ? undefined
      : {
          registro: "rms_i_4",
          topicPrefix,
          value: form.rms_i_4,
        },

    // Class II RMS values
    rms_ii_1: isNil(form.rms_ii_1)
      ? undefined
      : {
          registro: "rms_ii_1",
          topicPrefix,
          value: form.rms_ii_1,
        },
    rms_ii_2: isNil(form.rms_ii_2)
      ? undefined
      : {
          registro: "rms_ii_2",
          topicPrefix,
          value: form.rms_ii_2,
        },
    rms_ii_3: isNil(form.rms_ii_3)
      ? undefined
      : {
          registro: "rms_ii_3",
          topicPrefix,
          value: form.rms_ii_3,
        },
    rms_ii_4: isNil(form.rms_ii_4)
      ? undefined
      : {
          registro: "rms_ii_4",
          topicPrefix,
          value: form.rms_ii_4,
        },

    // Class III RMS values
    rms_iii_1: isNil(form.rms_iii_1)
      ? undefined
      : {
          registro: "rms_iii_1",
          topicPrefix,
          value: form.rms_iii_1,
        },
    rms_iii_2: isNil(form.rms_iii_2)
      ? undefined
      : {
          registro: "rms_iii_2",
          topicPrefix,
          value: form.rms_iii_2,
        },
    rms_iii_3: isNil(form.rms_iii_3)
      ? undefined
      : {
          registro: "rms_iii_3",
          topicPrefix,
          value: form.rms_iii_3,
        },
    rms_iii_4: isNil(form.rms_iii_4)
      ? undefined
      : {
          registro: "rms_iii_4",
          topicPrefix,
          value: form.rms_iii_4,
        },

    // Class IV RMS values
    rms_iv_1: isNil(form.rms_iv_1)
      ? undefined
      : {
          registro: "rms_iv_1",
          topicPrefix,
          value: form.rms_iv_1,
        },
    rms_iv_2: isNil(form.rms_iv_2)
      ? undefined
      : {
          registro: "rms_iv_2",
          topicPrefix,
          value: form.rms_iv_2,
        },
    rms_iv_3: isNil(form.rms_iv_3)
      ? undefined
      : {
          registro: "rms_iv_3",
          topicPrefix,
          value: form.rms_iv_3,
        },
    rms_iv_4: isNil(form.rms_iv_4)
      ? undefined
      : {
          registro: "rms_iv_4",
          topicPrefix,
          value: form.rms_iv_4,
        },

    // FFT Alarm values
    fft_f1_armonica: isNil(form.fft_f1_armonica)
      ? undefined
      : {
          registro: "fft_f1_armonica",
          topicPrefix,
          value: form.fft_f1_armonica,
        },
    fft_f1_soglia_1: isNil(form.fft_f1_soglia_1)
      ? undefined
      : {
          registro: "fft_f1_soglia_1",
          topicPrefix,
          value: form.fft_f1_soglia_1,
        },
    fft_f1_soglia_2: isNil(form.fft_f1_soglia_2)
      ? undefined
      : {
          registro: "fft_f1_soglia_2",
          topicPrefix,
          value: form.fft_f1_soglia_2,
        },
    fft_f1_guasto: isNil(form.fft_f1_guasto)
      ? undefined
      : {
          registro: "fft_f1_guasto",
          topicPrefix,
          value: form.fft_f1_guasto,
        },

    fft_f2_armonica: isNil(form.fft_f2_armonica)
      ? undefined
      : {
          registro: "fft_f2_armonica",
          topicPrefix,
          value: form.fft_f2_armonica,
        },
    fft_f2_soglia_1: isNil(form.fft_f2_soglia_1)
      ? undefined
      : {
          registro: "fft_f2_soglia_1",
          topicPrefix,
          value: form.fft_f2_soglia_1,
        },
    fft_f2_soglia_2: isNil(form.fft_f2_soglia_2)
      ? undefined
      : {
          registro: "fft_f2_soglia_2",
          topicPrefix,
          value: form.fft_f2_soglia_2,
        },
    fft_f2_guasto: isNil(form.fft_f2_guasto)
      ? undefined
      : {
          registro: "fft_f2_guasto",
          topicPrefix,
          value: form.fft_f2_guasto,
        },

    fft_f3_armonica: isNil(form.fft_f3_armonica)
      ? undefined
      : {
          registro: "fft_f3_armonica",
          topicPrefix,
          value: form.fft_f3_armonica,
        },
    fft_f3_soglia_1: isNil(form.fft_f3_soglia_1)
      ? undefined
      : {
          registro: "fft_f3_soglia_1",
          topicPrefix,
          value: form.fft_f3_soglia_1,
        },
    fft_f3_soglia_2: isNil(form.fft_f3_soglia_2)
      ? undefined
      : {
          registro: "fft_f3_soglia_2",
          topicPrefix,
          value: form.fft_f3_soglia_2,
        },
    fft_f3_guasto: isNil(form.fft_f3_guasto)
      ? undefined
      : {
          registro: "fft_f3_guasto",
          topicPrefix,
          value: form.fft_f3_guasto,
        },

    fft_f4_armonica: isNil(form.fft_f4_armonica)
      ? undefined
      : {
          registro: "fft_f4_armonica",
          topicPrefix,
          value: form.fft_f4_armonica,
        },
    fft_f4_soglia_1: isNil(form.fft_f4_soglia_1)
      ? undefined
      : {
          registro: "fft_f4_soglia_1",
          topicPrefix,
          value: form.fft_f4_soglia_1,
        },
    fft_f4_soglia_2: isNil(form.fft_f4_soglia_2)
      ? undefined
      : {
          registro: "fft_f4_soglia_2",
          topicPrefix,
          value: form.fft_f4_soglia_2,
        },
    fft_f4_guasto: isNil(form.fft_f4_guasto)
      ? undefined
      : {
          registro: "fft_f4_guasto",
          topicPrefix,
          value: form.fft_f4_guasto,
        },

    fft_f5_armonica: isNil(form.fft_f5_armonica)
      ? undefined
      : {
          registro: "fft_f5_armonica",
          topicPrefix,
          value: form.fft_f5_armonica,
        },
    fft_f5_soglia_1: isNil(form.fft_f5_soglia_1)
      ? undefined
      : {
          registro: "fft_f5_soglia_1",
          topicPrefix,
          value: form.fft_f5_soglia_1,
        },
    fft_f5_soglia_2: isNil(form.fft_f5_soglia_2)
      ? undefined
      : {
          registro: "fft_f5_soglia_2",
          topicPrefix,
          value: form.fft_f5_soglia_2,
        },
    fft_f5_guasto: isNil(form.fft_f5_guasto)
      ? undefined
      : {
          registro: "fft_f5_guasto",
          topicPrefix,
          value: form.fft_f5_guasto,
        },
  };

  return Object.values(_values).filter(
    (v) => v !== undefined,
  ) as UpdateRegistroDto[];
}

function createRmsInputCell(
  control: Control<Registri_PRAEVIDERE_VIBRATIO_35>,
  name: keyof Registri_PRAEVIDERE_VIBRATIO_35,
  prefix: string | null,
  t: TFunction,
  readOnly: boolean = false,
) {
  return (
    <TableCell>
      <Controller
        control={control}
        name={name}
        rules={{
          min: {
            value: 0,
            message: t("praevidereVibratio35.errori.maggioreDi", { min: 0 }),
          },
          max: {
            value: 500,
            message: t("praevidereVibratio35.errori.minoreDi", { max: 500 }),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="center">
            <NumberInput
              {...field}
              size="small"
              disabled={readOnly}
              numericInputProps={{
                decimalScale: 2,
              }}
              startAdornment={prefix ? <Box mr={1}>{prefix}</Box> : null}
              sx={{ display: "flex" }}
            />
            {error && (
              <FormHelperText
                error
                sx={{ textAlign: "center", fontSize: "0.7rem" }}
              >
                {error?.message}
              </FormHelperText>
            )}
          </Stack>
        )}
      />
    </TableCell>
  );
}

function createFftArmonicaCell(
  control: Control<Registri_PRAEVIDERE_VIBRATIO_35>,
  name: keyof Registri_PRAEVIDERE_VIBRATIO_35,
  t: TFunction,
  readOnly: boolean = false,
) {
  return (
    <TableCell align="center">
      <Controller
        control={control}
        name={name}
        rules={{
          min: {
            value: 0,
            message: t("praevidereVibratio35.errori.intervallo", {
              min: 0,
              max: 5000,
            }),
          },
          max: {
            value: 5000,
            message: t("praevidereVibratio35.errori.intervallo", {
              min: 0,
              max: 5000,
            }),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="center">
            <NumberInput
              {...field}
              size="small"
              sx={{ maxWidth: "100px" }}
              disabled={readOnly}
              numericInputProps={{
                decimalScale: 2,
              }}
            />
            {error && (
              <FormHelperText
                error
                sx={{ textAlign: "center", fontSize: "0.7rem" }}
              >
                {error?.message}
              </FormHelperText>
            )}
          </Stack>
        )}
      />
    </TableCell>
  );
}

function createFftSogliaCell(
  control: Control<Registri_PRAEVIDERE_VIBRATIO_35>,
  name: keyof Registri_PRAEVIDERE_VIBRATIO_35,
  t: TFunction,
  readOnly: boolean = false,
) {
  return (
    <TableCell align="center">
      <Controller
        control={control}
        name={name}
        rules={{
          min: {
            value: 0,
            message: t("praevidereVibratio35.errori.intervallo", {
              min: 0,
              max: 10,
            }),
          },
          max: {
            value: 10,
            message: t("praevidereVibratio35.errori.intervallo", {
              min: 0,
              max: 10,
            }),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="center">
            <NumberInput
              {...field}
              size="small"
              sx={{ maxWidth: "100px" }}
              disabled={readOnly}
              numericInputProps={{
                decimalScale: 2,
              }}
            />
            {error && (
              <FormHelperText
                error
                sx={{ textAlign: "center", fontSize: "0.7rem" }}
              >
                {error?.message}
              </FormHelperText>
            )}
          </Stack>
        )}
      />
    </TableCell>
  );
}

function createFftGuastoCell(
  control: Control<Registri_PRAEVIDERE_VIBRATIO_35>,
  name: keyof Registri_PRAEVIDERE_VIBRATIO_35,
  t: TFunction,
  readOnly: boolean = false,
) {
  return (
    <TableCell align="center">
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            size="small"
            fullWidth
            disabled={readOnly}
            error={!!error}
            helperText={error?.message}
            placeholder={t("praevidereVibratio35.campi.descrizioneGuasto")}
          />
        )}
      />
    </TableCell>
  );
}

export function EditorRegistri_PRAEVIDERE_VIBRATIO_35({
  control: c,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_PRAEVIDERE_VIBRATIO_35>;
  errors: FieldErrors<Registri_PRAEVIDERE_VIBRATIO_35>;
  register: UseFormRegister<Registri_PRAEVIDERE_VIBRATIO_35>;
  getValues: UseFormGetValues<Registri_PRAEVIDERE_VIBRATIO_35>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Machine Type Section */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("praevidereVibratio35.sezioni.tipologiaMacchina")}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  gutterBottom
                >
                  {t("praevidereVibratio35.campi.tipoMacchina")}
                </Typography>
                <FormControl fullWidth>
                  <Controller
                    control={c}
                    name="tipologia_macchina"
                    rules={{
                      required: t(
                        "praevidereVibratio35.errori.selezionaTipoMacchina",
                      ),
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        labelId="tipologia-macchina-label"
                        label={t("praevidereVibratio35.campi.tipoMacchina")}
                        disabled={readOnly}
                        error={!!error}
                        value={field.value || ""}
                      >
                        {TIPOLOGIA_MACCHINA_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.getLabel(t)}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.tipologia_macchina && (
                    <FormHelperText error>
                      {errors.tipologia_macchina.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  control={c}
                  name="velocita_rotazione"
                  rules={{
                    required: t(
                      "praevidereVibratio35.errori.inserisciVelocitaRotazione",
                    ),
                    min: {
                      value: 0,
                      message: t("praevidereVibratio35.errori.velocitaMin", {
                        min: 0,
                      }),
                    },
                    max: {
                      value: 100_000,
                      message: t("praevidereVibratio35.errori.velocitaMax", {
                        max: 100_000,
                      }),
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Stack direction="column">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {t("praevidereVibratio35.campi.velocitaRotazione")}
                      </Typography>
                      <NumberInput
                        {...field}
                        sx={{ width: "100%" }}
                        disabled={readOnly}
                        numericInputProps={{
                          decimalScale: 0,
                        }}
                        placeholder={t(
                          "praevidereVibratio35.placeholder.velocitaRotazione",
                        )}
                      />
                      {error && (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          {error?.message}
                        </FormHelperText>
                      )}
                    </Stack>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Allarmi RMS */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("praevidereVibratio35.sezioni.allarmiRms")}
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <Table size="small">
                <Box component="thead">
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.classe")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.verde")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.giallo")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.arancio")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.rosso")}
                    </TableCell>
                  </TableRow>
                </Box>
                <TableBody>
                  {/* Class I */}
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      I
                    </TableCell>
                    {createRmsInputCell(c, "rms_i_1", "≤", t, readOnly)}
                    {createRmsInputCell(c, "rms_i_2", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_i_3", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_i_4", ">", t, readOnly)}
                  </TableRow>

                  {/* Class II */}
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      II
                    </TableCell>
                    {createRmsInputCell(c, "rms_ii_1", "≤", t, readOnly)}
                    {createRmsInputCell(c, "rms_ii_2", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_ii_3", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_ii_4", ">", t, readOnly)}
                  </TableRow>

                  {/* Class III */}
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      III
                    </TableCell>
                    {createRmsInputCell(c, "rms_iii_1", "≤", t, readOnly)}
                    {createRmsInputCell(c, "rms_iii_2", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_iii_3", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_iii_4", ">", t, readOnly)}
                  </TableRow>

                  {/* Class IV */}
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      IV
                    </TableCell>
                    {createRmsInputCell(c, "rms_iv_1", "≤", t, readOnly)}
                    {createRmsInputCell(c, "rms_iv_2", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_iv_3", "", t, readOnly)}
                    {createRmsInputCell(c, "rms_iv_4", ">", t, readOnly)}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Card>

      {/* Allarmi FFT */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("praevidereVibratio35.sezioni.allarmiFft")}
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <Table size="small">
                <Box component="thead">
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.allarme")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.armonica")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.soglia1")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.soglia2")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {t("praevidereVibratio35.tabella.guasto")}
                    </TableCell>
                  </TableRow>
                </Box>
                <TableBody>
                  <TableRow>
                    <TableCell align="center">1</TableCell>
                    {createFftArmonicaCell(c, "fft_f1_armonica", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f1_soglia_1", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f1_soglia_2", t, readOnly)}
                    {createFftGuastoCell(c, "fft_f1_guasto", t, readOnly)}
                  </TableRow>

                  <TableRow>
                    <TableCell align="center">2</TableCell>
                    {createFftArmonicaCell(c, "fft_f2_armonica", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f2_soglia_1", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f2_soglia_2", t, readOnly)}
                    {createFftGuastoCell(c, "fft_f2_guasto", t, readOnly)}
                  </TableRow>

                  <TableRow>
                    <TableCell align="center">3</TableCell>
                    {createFftArmonicaCell(c, "fft_f3_armonica", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f3_soglia_1", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f3_soglia_2", t, readOnly)}
                    {createFftGuastoCell(c, "fft_f3_guasto", t, readOnly)}
                  </TableRow>

                  <TableRow>
                    <TableCell align="center">4</TableCell>
                    {createFftArmonicaCell(c, "fft_f4_armonica", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f4_soglia_1", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f4_soglia_2", t, readOnly)}
                    {createFftGuastoCell(c, "fft_f4_guasto", t, readOnly)}
                  </TableRow>

                  <TableRow>
                    <TableCell align="center">5</TableCell>
                    {createFftArmonicaCell(c, "fft_f5_armonica", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f5_soglia_1", t, readOnly)}
                    {createFftSogliaCell(c, "fft_f5_soglia_2", t, readOnly)}
                    {createFftGuastoCell(c, "fft_f5_guasto", t, readOnly)}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
